import { useState, useEffect } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { Route } from 'config';
import { runsWebview } from 'utils/device';
import { postMessage, WebViewMessage } from 'utils/webview';
import { getQueryParam } from 'utils/location';

import useCurrentUser from 'hooks/useCurrentUser';
import useAddresses from 'hooks/useAddresses';
import useNotifications from 'hooks/useNotifications';
import { UpcomingMenusProvider } from 'contexts/UpcomingMenus';
import { NotificationType } from 'models/Notifications';

import ReactivationBanner from 'components/shared/banners/ReactivationBanner';
import AddressBanner from 'components/shared/banners/AddressBanner';
import UpdateAddressBanner from 'components/shared/banners/UpdateAddressBanner';
import ExpiringPaymentBanner from 'components/shared/banners/ExpiringPaymentBanner';
import AddressModal from 'components/shared/modals/AddressModal';

import PlanCard from './cards/PlanCard';
import AccountSettingsCard from './cards/AccountSettingsCard';
import PaymentInformationCard from './cards/PaymentInformationCard';
import DeliveryAddressesCard from './cards/DeliveryAddressesCard';
import NotificationsDisclaimerCard from './cards/NotificationsDisclaimerCard';

function Profile(): ReactElement {
  const navigate = useNavigate();
  const { notifications, dispatch } = useNotifications();
  const { addressChange } = notifications;
  const { addresses, reload: reloadAddresses } = useAddresses();
  const { currentUser, reload: reloadUser } = useCurrentUser();
  const { isEmpty, isDeactivated, hasValidPaymentMethod } = currentUser;

  const hasValidPrimaryAddress = addresses[0]?.isValid;
  const [showAddressModal, setShowAddressModal] = useState(false);
  const sectionToShow = getQueryParam('section');

  const classForCard = (key) => {
    const hideCard = runsWebview && sectionToShow && sectionToShow !== key;
    return hideCard ? 'd-mapp-none' : '';
  };
  const hideForMobileAppClass = runsWebview && sectionToShow ? 'd-mapp-none' : '';

  const handleExpiringPayment = () => {
    if (runsWebview) {
      postMessage(WebViewMessage.PAYMENT_INFO_REQUESTED);
    } else {
      navigate(Route.PROFILE_EDIT_PAYMENT);
    }
  };

  const handleUpdateAddress = () => {
    if (runsWebview) {
      postMessage(WebViewMessage.SHIPPING_ADDRESS_REQUESTED);
    } else {
      setShowAddressModal(true);
    }
  };

  // Remove ADDRESS_CHANGE notifications on unmount, otherwise
  // they will also appear in upcoming menus page.
  useEffect(() => (
    () => dispatch({
      type: 'removeNotifications',
      notificationType: NotificationType.ADDRESS_CHANGE,
    })
  ), [dispatch]);

  return (
    <div className="col col-md-9 col-lg-10">
      <div className="c-profile">
        <h1
          className={[
            'c-heading',
            'c-heading--center',
            'c-heading--brand-4',
            hideForMobileAppClass,
          ].join(' ')}
        >
          My Account
        </h1>

        {addressChange && <AddressBanner />}

        {!isEmpty && !isDeactivated && !hasValidPaymentMethod && (
          <ExpiringPaymentBanner
            onClick={handleExpiringPayment}
          />
        )}

        {addresses[0] && !isDeactivated && hasValidPaymentMethod
        && !hasValidPrimaryAddress && (
          <UpdateAddressBanner onClick={handleUpdateAddress} />
        )}

        <UpcomingMenusProvider>
          {!isEmpty && isDeactivated
            && <ReactivationBanner btnClassName={hideForMobileAppClass} onPage />}

          <div className="row c-profile__cards-list">
            <AccountSettingsCard className={classForCard('settings')} />
            <PaymentInformationCard className={classForCard('payment')} />
            <PlanCard className={classForCard('plan')} />
            <DeliveryAddressesCard className={classForCard('addresses')} />
            <NotificationsDisclaimerCard className={classForCard('notifications')} />
          </div>
        </UpcomingMenusProvider>
      </div>

      {showAddressModal && (
        <AddressModal
          isPrimaryEditable={false}
          initialAddress={addresses?.[0]}
          onClose={() => {
            setShowAddressModal(false);
            reloadAddresses();
            reloadUser();
          }}
        />
      )}
    </div>
  );
}

export default Profile;
